/**
 * One shift, with everything recorded about it — and the adjustment flow.
 *
 * The reason field is mandatory here AND at the API. A rule enforced only in the UI is
 * not a rule, and an employee-monitoring system where hours can be silently rewritten
 * is one nobody should trust.
 */

import { lazy, Suspense, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { Badge, Card, ErrorNotice, Field, PageHeader, Spinner, StatusDot } from '@/components/ui';
import { useDir, useI18n, type TranslationKey } from '@/i18n';
import { api, ApiError, type Shift, type Site } from '@/lib/api';
import { dateLong, dateTime, distance, duration, fromMilan, fullName, time } from '@/lib/format';
import { useLabel } from '@/lib/useLabel';

const MIN_REASON = 10;

// MapLibre is large; only this page and the site form load it.
const ShiftMap = lazy(() => import('@/components/ShiftMap').then((m) => ({ default: m.ShiftMap })));

const point = (lat?: number | null, lng?: number | null, accuracyM?: number | null) =>
  lat != null && lng != null ? { lat, lng, accuracyM: accuracyM ?? null } : null;

/** A link that opens a position on OpenStreetMap, for a closer look or to share. */
const mapLink = (lat: number, lng: number) =>
  `https://www.openstreetmap.org/?mlat=${lat}&mlon=${lng}#map=18/${lat}/${lng}`;

/** The map, with its inputs memoised so it is not rebuilt on every render of the page. */
function ShiftLocationMap({ shift, site }: { shift: Shift; site: Site | undefined }) {
  const { t } = useI18n();
  const fence = useMemo(
    () =>
      site && site.lat !== null && site.lng !== null
        ? { lat: site.lat, lng: site.lng, radiusM: site.geofenceRadiusM, polygon: site.geofencePolygon }
        : null,
    [site],
  );
  const clockIn = useMemo(
    () => point(shift.clockInLat, shift.clockInLng, shift.clockInAccuracyM),
    [shift.clockInLat, shift.clockInLng, shift.clockInAccuracyM],
  );
  const clockOut = useMemo(
    () => point(shift.clockOutLat, shift.clockOutLng, shift.clockOutAccuracyM),
    [shift.clockOutLat, shift.clockOutLng, shift.clockOutAccuracyM],
  );
  const labels = useMemo(
    () => ({ site: shift.siteName ?? t('common.site'), clockIn: t('shifts.clockIn'), clockOut: t('shifts.clockOut') }),
    [shift.siteName, t],
  );

  if (!fence && !clockIn && !clockOut) return null;
  return (
    <div className="mb-4">
      <Suspense fallback={<div className="h-72 rounded border border-border grid place-items-center text-sm text-muted">{t('common.loading')}</div>}>
        <ShiftMap site={fence} clockIn={clockIn} clockOut={clockOut} labels={labels} />
      </Suspense>
      <div className="flex flex-wrap gap-4 mt-2 text-xs text-muted">
        <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ background: '#15803d' }} />{t('shifts.clockIn')}</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ background: '#b20307' }} />{t('shifts.clockOut')}</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-3 h-0 border-t border-dashed" style={{ borderColor: '#b20307' }} />{t('shifts.siteArea')}</span>
      </div>
    </div>
  );
}

/** "45.48170, 9.20060 · ±12 m · Open in map" */
function Position({ lat, lng, accuracyM, openLabel }: { lat?: number | null; lng?: number | null; accuracyM?: number | null; openLabel: string }) {
  if (lat == null || lng == null) return <span className="text-subtle">—</span>;
  return (
    <span className="tabular">
      {lat.toFixed(5)}, {lng.toFixed(5)}
      {accuracyM != null ? ` · ±${Math.round(accuracyM)} m` : ''}
      {' · '}
      <a href={mapLink(lat, lng)} target="_blank" rel="noreferrer" className="text-primary hover:underline">
        {openLabel}
      </a>
    </span>
  );
}

const ADJUSTABLE = [
  { field: 'clock_out_at', labelKey: 'shifts.clockOut', type: 'datetime-local' },
  { field: 'clock_in_at', labelKey: 'shifts.clockIn', type: 'datetime-local' },
  { field: 'break_minutes', labelKey: 'shifts.breakMinutes', type: 'number' },
] as const;

/**
 * Adjustment values are stored as raw strings so the audit trail records exactly what
 * was written. Format them for display — a manager should not have to read
 * "Fri Sep 11 2026 21:58:00 GMT+0200" to understand what changed.
 */
function formatAdjustmentValue(fieldName: string, value: string | null): string {
  if (!value) return '—';
  if (!fieldName.endsWith('_at')) return value;

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : dateTime(parsed);
}

/* Keys, resolved at render: this maps an audit row's column name to a human label, and
   the audit trail is read in whichever language the reader has chosen. */
const FIELD_LABELS: Record<string, TranslationKey | undefined> = {
  clock_in_at: 'shifts.clockIn',
  clock_out_at: 'shifts.clockOut',
  break_minutes: 'shifts.breakMinutes',
  job_id: 'shifts.job',
  status: 'common.status',
};

function Row({ label: text, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline gap-4 py-2 border-b border-border last:border-0">
      <span className="text-sm text-muted w-44 shrink-0">{text}</span>
      <span className="text-sm font-medium flex-1 min-w-0">{children}</span>
    </div>
  );
}

export function ShiftDetailPage() {
  const { t, plural } = useI18n();
  const { forward } = useDir();
  const L = useLabel();
  const { id = '' } = useParams();
  const queryClient = useQueryClient();
  const [reviewNote, setReviewNote] = useState('');

  const [field, setField] = useState<string>('clock_out_at');
  const [value, setValue] = useState('');
  const [reason, setReason] = useState('');
  const [saved, setSaved] = useState(false);

  const shift = useQuery({ queryKey: ['shift', id], queryFn: () => api.shift(id) });
  const sites = useQuery({ queryKey: ['sites'], queryFn: api.sites });
  const users = useQuery({ queryKey: ['users'], queryFn: () => api.users() });

  const adjust = useMutation({
    mutationFn: () =>
      api.adjustShift(id, {
        field,
        // datetime-local gives a wall-clock time with no zone. It is Milan's, whatever
        // the computer is set to; the server stores the instant.
        value: field.endsWith('_at') ? fromMilan(value) : value,
        reason: reason.trim(),
      }),
    onSuccess: async () => {
      setValue('');
      setReason('');
      setSaved(true);
      await queryClient.invalidateQueries({ queryKey: ['shift', id] });
      await queryClient.invalidateQueries({ queryKey: ['shifts'] });
      setTimeout(() => setSaved(false), 4000);
    },
  });

  /*
   * Sign-off, and taking it back.
   *
   * Both invalidate the same queries as an adjustment, because both change whether
   * this shift shows in the queue, on the dashboard, and in the payroll total — and a
   * screen that still says "3 to review" after you cleared one is the reason nobody
   * trusts the number.
   */
  const refreshAfterReview = async () => {
    setReviewNote('');
    await queryClient.invalidateQueries({ queryKey: ['shift', id] });
    await queryClient.invalidateQueries({ queryKey: ['shifts'] });
    await queryClient.invalidateQueries({ queryKey: ['dashboard'] });
  };

  const review = useMutation({
    mutationFn: () => api.reviewShift(id, reviewNote.trim() || undefined),
    onSuccess: refreshAfterReview,
  });

  const unreview = useMutation({
    mutationFn: () => api.unreviewShift(id),
    onSuccess: refreshAfterReview,
  });

  if (shift.isLoading) return <Spinner />;
  if (shift.error) return <ErrorNotice error={shift.error} />;
  if (!shift.data) return null;

  const s = shift.data.shift;
  const adjustments = shift.data.adjustments;
  const user = users.data?.users.find((u) => u.id === s.userId);
  const status = L('shift', s.status);

  const canSubmit = value.trim() !== '' && reason.trim().length >= MIN_REASON && !adjust.isPending;

  return (
    <>
      <PageHeader
        title={user ? fullName(user) : t('common.shift')}
        subtitle={dateLong(s.clockInAt)}
        actions={
          <>
            <Link to="/shifts" className="btn-secondary">
              {t('common.backToShifts')}
            </Link>
            <Badge tone={status.tone}>{status.label}</Badge>
          </>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <div className="label-text mb-1">{t('shifts.recordedDuration')}</div>
            <div className="text-4xl font-bold tabular tracking-tight">
              {duration(s.durationMinutes)}
            </div>
            {/* Which job, and where. */}
            <div className="mt-3 text-sm">
              {s.jobId && s.jobReference ? (
                <Link to={`/jobs/${s.jobId}`} className="font-semibold hover:text-primary">
                  {s.jobReference} · {s.jobTitle}
                </Link>
              ) : (
                <span className="text-subtle">{t('shifts.noJob')}</span>
              )}
              <div className="text-muted">
                {s.siteName ?? '—'}
                {s.siteAddress ? ` · ${s.siteAddress}` : ''}
              </div>
            </div>
            <div className="text-sm text-muted tabular mt-1">
              {time(s.clockInAt)}
              {s.clockOutAt ? ` – ${time(s.clockOutAt)}` : ` – ${t('shifts.inProgressSuffix')}`}
              {s.breakMinutes > 0 ? ` · ${t('shifts.breakShort', { count: s.breakMinutes })}` : ''}
            </div>

            {s.exceptionFlags.length > 0 ? (
              <div className="flex flex-wrap gap-1.5 mt-4">
                {s.exceptionFlags.map((f) => {
                  const spec = L('flag', f);
                  return (
                    <Badge key={f} tone={spec.tone}>
                      {spec.label}
                    </Badge>
                  );
                })}
              </div>
            ) : null}
          </Card>

          {/* Everything the device reported. This is the evidence in a dispute, so it
              is shown in full rather than summarised into a verdict. */}
          <Card>
            <h2 className="font-bold mb-3">{t('shifts.locationData')}</h2>

            <ShiftLocationMap shift={s} site={(sites.data?.sites ?? []).find((x) => x.id === s.siteId)} />

            <Row label={t('shifts.inOutcome')}>
              {s.clockInWithinGeofence === true ? (
                <StatusDot tone="success">{t('shifts.onSite')}</StatusDot>
              ) : s.clockInWithinGeofence === false ? (
                <StatusDot tone="warning">{t('common.outsideArea')}</StatusDot>
              ) : (
                <StatusDot tone="neutral">{t('shifts.noGps')}</StatusDot>
              )}
            </Row>
            <Row label={t('shifts.inDistance')}>
              <span className="tabular">{distance(s.clockInDistanceM)}</span>
            </Row>
            <Row label={t('shifts.inPosition')}>
              <Position lat={s.clockInLat} lng={s.clockInLng} accuracyM={s.clockInAccuracyM} openLabel={t('shifts.openInMap')} />
            </Row>
            <Row label={t('shifts.outOutcome')}>
              {s.clockOutWithinGeofence === true ? (
                <StatusDot tone="success">{t('shifts.onSite')}</StatusDot>
              ) : s.clockOutWithinGeofence === false ? (
                <StatusDot tone="warning">{t('common.outsideArea')}</StatusDot>
              ) : (
                <StatusDot tone="neutral">{t('shifts.noGps')}</StatusDot>
              )}
            </Row>
            <Row label={t('shifts.outDistance')}>
              <span className="tabular">{distance(s.clockOutDistanceM)}</span>
            </Row>
            <Row label={t('shifts.outPosition')}>
              <Position lat={s.clockOutLat} lng={s.clockOutLng} accuracyM={s.clockOutAccuracyM} openLabel={t('shifts.openInMap')} />
            </Row>
            {s.overrideReason ? (
              <Row label={t('common.reasonGiven')}>{s.overrideReason}</Row>
            ) : null}
            {s.clientEventId ? (
              <Row label={t('shifts.deviceEventId')}>
                <code className="text-xs text-muted">{s.clientEventId}</code>
              </Row>
            ) : null}

            <p className="text-xs text-subtle mt-4 leading-relaxed">
              {t('shifts.locationHint1')} {t('shifts.locationHint2')}
            </p>
          </Card>

          <Card>
            <h2 className="font-bold mb-3">{t('shifts.adjustments')}</h2>
            {adjustments.length === 0 ? (
              <p className="text-sm text-muted">
                {t('shifts.noAdjustmentsHint')}
              </p>
            ) : (
              <ul className="space-y-4">
                {adjustments.map((a) => (
                  <li key={a.id} className="border-b border-border last:border-0 pb-4 last:pb-0">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <span className="font-semibold text-sm">
                        {(() => {
                          const key = FIELD_LABELS[a.field_name];
                          // An audit row can name a column this map has not been taught
                          // yet; showing the raw column beats showing nothing.
                          return key ? t(key) : a.field_name;
                        })()}
                      </span>
                      <span className="text-sm text-muted line-through tabular">
                        {formatAdjustmentValue(a.field_name, a.old_value)}
                      </span>
                      <span className="text-muted">{forward}</span>
                      <span className="text-sm font-semibold tabular">
                        {formatAdjustmentValue(a.field_name, a.new_value)}
                      </span>
                    </div>
                    <p className="text-sm text-muted mt-1">{a.reason}</p>
                    <p className="text-xs text-subtle mt-1">
                      {a.adjusted_by_name} · {dateTime(a.adjusted_at)}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>

        <div className="space-y-6">
          {/*
            Review first, adjustment second, and that order is the point.
            Most flagged shifts are correct — a poor GPS fix in a basement, a forgotten
            clock-out the office can confirm by phone. Until this card existed the only
            way to clear one was to ADJUST it, which writes a correction claiming a
            change that never happened. Putting the honest action first, and making it
            the primary button, is what stops the audit trail filling with fiction.
          */}
          <Card>
            <h2 className="font-bold mb-1">{t('shifts.reviewTitle')}</h2>

            {s.reviewedAt ? (
              <>
                <p className="text-sm text-success font-semibold mt-2">
                  {t('shifts.reviewedBy', {
                    name: s.reviewedByName ?? '—',
                    date: dateTime(s.reviewedAt),
                  })}
                </p>
                {s.reviewNote ? (
                  <p className="text-sm text-muted mt-1 leading-relaxed">{s.reviewNote}</p>
                ) : null}

                <button
                  type="button"
                  className="btn-secondary mt-4"
                  disabled={unreview.isPending}
                  onClick={() => unreview.mutate()}
                >
                  {unreview.isPending ? t('common.saving') : t('shifts.withdrawReview')}
                </button>
              </>
            ) : (
              <>
                <p className="text-xs text-muted mb-4 leading-relaxed">
                  {t('shifts.reviewExplainer')}
                </p>

                <Field label={t('shifts.reviewNote')}>
                  <textarea
                    className="input h-20 py-2 resize-none"
                    value={reviewNote}
                    placeholder={t('shifts.reviewNotePlaceholder')}
                    onChange={(e) => setReviewNote(e.target.value)}
                  />
                </Field>

                <button
                  type="button"
                  className="btn-primary w-full mt-3"
                  disabled={review.isPending || s.status === 'open'}
                  onClick={() => review.mutate()}
                >
                  {review.isPending ? t('common.saving') : t('shifts.review')}
                </button>
              </>
            )}

            {review.error ? <ErrorNotice error={review.error} /> : null}
            {unreview.error ? <ErrorNotice error={unreview.error} /> : null}
          </Card>

          <Card>
            <h2 className="font-bold mb-1">{t('shifts.correct')}</h2>
            <p className="text-xs text-muted mb-4 leading-relaxed">
              {t('shifts.correctHint')}
            </p>

            <div className="space-y-3">
              <Field label={t('common.field')}>
                <select
                  className="input"
                  value={field}
                  onChange={(e) => {
                    setField(e.target.value);
                    setValue('');
                  }}
                >
                  {ADJUSTABLE.map((a) => (
                    <option key={a.field} value={a.field}>
                      {t(a.labelKey)}
                    </option>
                  ))}
                </select>
              </Field>

              <Field label={t('common.newValue')}>
                <input
                  className="input"
                  type={ADJUSTABLE.find((a) => a.field === field)?.type ?? 'text'}
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                />
              </Field>

              <Field
                label={t('common.reasonRequired')}
                hint={
                  reason.trim().length < MIN_REASON
                    ? plural(
                        MIN_REASON - reason.trim().length,
                        'common.charsLeftOne',
                        'common.charsLeftMany',
                      )
                    : undefined
                }
              >
                <textarea
                  className="input h-24 py-2 resize-none"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder={t('shifts.reasonPlaceholder')}
                />
              </Field>

              {adjust.error ? (
                <p className="text-sm text-danger">
                  {adjust.error instanceof ApiError ? adjust.error.message : t('common.saveError')}
                </p>
              ) : null}

              {saved ? (
                <p className="text-sm text-success font-semibold">{t('shifts.adjustmentSaved')}</p>
              ) : null}

              <button
                type="button"
                className="btn-primary w-full"
                disabled={!canSubmit}
                onClick={() => adjust.mutate()}
              >
                {adjust.isPending ? t('common.saving') : t('shifts.saveAdjustment')}
              </button>
            </div>
          </Card>
        </div>
      </div>
    </>
  );
}
